import { FC, useCallback, useMemo } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Column } from '@material-table/core';
import DataTable from '../../components/shared/DataTable/DataTable';
import { formatShortDay } from '../page-supplier-evaluations/reviewFormat';
import {
  ByProjectPage,
  OverviewApiQuery,
  ProjectGroup,
  serviceEvaluations,
} from '../../services/service-evaluations';
import {
  EvaluationContextType,
  EvaluationLimits,
  EvaluationOverviewSort,
} from '../../util/evaluationContract';
import { formatScore } from '../../util/evaluationFormat';
import ProjectGroupPanel from './ProjectGroupPanel';
import RowChevron from './RowChevron';
import { OverviewTableProps, SortFields, isPanelOpen, useRemoteOverview } from './remoteTable';
import styles from './app-evaluation-overview.module.scss';

const load = (query: OverviewApiQuery) => serviceEvaluations.getByProject(query);
const rowsOf = (page: ByProjectPage) => page.groups;
const keyOf = (group: ProjectGroup) => group.key;

const SORT_FIELDS: SortFields = {
  [EvaluationOverviewSort.Name]: 'name',
  [EvaluationOverviewSort.Score]: 'score',
  [EvaluationOverviewSort.LastActivity]: 'lastActivityDay',
};

const groupTitle = (t: TFunction, group: ProjectGroup) => {
  if (group.type === EvaluationContextType.Historical) {
    const name =
      group.name && group.number ? `${group.name} (${group.number})` : group.name || group.number;
    return t('overview.group.historical', 'Historiskt: {{name}}', { name: name ?? '' });
  }
  if (group.type === EvaluationContextType.None) return t('overview.group.none', 'Utan projekt');
  return group.name ?? '';
};

const groupSub = (t: TFunction, group: ProjectGroup) => {
  if (group.type === EvaluationContextType.Historical) {
    return t('overview.group.historicalSub', 'Historiskt projekt');
  }
  if (group.type === EvaluationContextType.None) {
    return t('overview.group.noneSub', 'Utvärderingar utan projekt');
  }
  return [
    group.number &&
      t('overview.group.number', 'Projektnummer {{number}}', { number: group.number }),
    group.isDeleted && t('overview.group.deleted', '(borttaget projekt)'),
    group.ownOnly && t('overview.group.ownOnly', 'du ser bara utvärderingar du själv har skapat'),
  ]
    .filter(Boolean)
    .join(' · ');
};

const supplierSummary = (t: TFunction, group: ProjectGroup) =>
  [
    t('overview.group.suppliers', {
      count: group.supplierCount,
      defaultValue_one: '{{count}} leverantör',
      defaultValue_other: '{{count}} leverantörer',
    }),
    t('overview.group.active', {
      count: group.activeCount,
      defaultValue_one: '{{count}} pågående',
      defaultValue_other: '{{count}} pågående',
    }),
    group.toEvaluateCount > 0 &&
      t('overview.group.toEvaluate', {
        count: group.toEvaluateCount,
        defaultValue_one: '{{count}} ej utvärderad',
        defaultValue_other: '{{count}} ej utvärderade',
      }),
  ]
    .filter(Boolean)
    .join(' · ');

// Per leverantör's EvaluationRating without its answer count, which a group does not carry.
const GroupRating: FC<{ score: number | null }> = ({ score }) => {
  const [t] = useTranslation('evaluations');
  if (score === null) {
    const notRated = t('supplierScore.notRated', 'Inte betygsatt');
    return (
      <span className={styles.muted} title={notRated} aria-label={notRated}>
        –
      </span>
    );
  }
  return (
    <span className={`gray-star ${styles.rating}`}>
      <span>{formatScore(score)}</span>
      <span className="fa fa-star" aria-hidden="true" />
    </span>
  );
};

const ProjectGroupTable: FC<OverviewTableProps> = ({
  query,
  canWrite,
  onLoaded,
  onFailed,
  onOpenChange,
  onPageChange,
  onPageRestored,
  onSortChange,
  onStart,
}) => {
  const [t, i18n] = useTranslation('evaluations');

  const renderPanel = useCallback(
    ({ rowData }: { rowData: ProjectGroup }) => (
      <ProjectGroupPanel group={rowData} canWrite={canWrite} onStart={onStart} />
    ),
    [canWrite, onStart],
  );

  const { tableRef, fetchRows, onRowClick, onOrderChange, header, totalCount } = useRemoteOverview({
    view: 'project',
    query,
    load,
    rowsOf,
    keyOf,
    renderPanel,
    onLoaded: (page, loadedQuery) =>
      onLoaded(
        {
          counts: page.counts,
          batch: page.batch,
          focus: null,
          totalCount: page.totalCount,
        },
        loadedQuery,
      ),
    onFailed,
    onOpenChange,
    onPageRestored,
    onSortChange,
    sortFields: SORT_FIELDS,
  });

  const columns: Column<ProjectGroup>[] = useMemo(
    () => [
      {
        title: '',
        width: 40,
        sorting: false,
        render: group => <RowChevron open={isPanelOpen(group)} label={groupTitle(t, group)} />,
      },
      {
        title: t('overview.columns.project', 'Projekt'),
        field: 'name',
        render: group => {
          const title = groupTitle(t, group);
          const sub = groupSub(t, group);
          return (
            <div className={styles.nameCell}>
              <span className={styles.name} title={title}>
                {title}
              </span>
              {sub && <span className={`${styles.small} ${styles.muted}`}>{sub}</span>}
            </div>
          );
        },
      },
      {
        title: t('overview.columns.suppliers', 'Leverantörer'),
        field: 'supplierCount',
        sorting: false,
        render: group => {
          const summary = supplierSummary(t, group);
          return (
            <span className={styles.ellipsis} title={summary}>
              {summary}
            </span>
          );
        },
      },
      {
        title: t('overview.columns.score', 'Betyg'),
        field: 'score',
        width: 140,
        render: group => <GroupRating score={group.score} />,
      },
      {
        title: t('overview.columns.lastActivity', 'Senaste aktivitet'),
        field: 'lastActivityDay',
        width: 180,
        render: group => (
          <span className={styles.tabular}>
            {formatShortDay(group.lastActivityDay, i18n.language)}
          </span>
        ),
      },
    ],
    [t, i18n.language],
  );

  const sortField = header.sort && SORT_FIELDS[header.sort.key];
  const sortColumn = sortField ? columns.findIndex(column => column.field === sortField) : -1;

  return (
    <DataTable<ProjectGroup>
      key={header.mount}
      tableRef={tableRef}
      columns={columns}
      data={fetchRows}
      isLoading={false}
      defaultOrderBy={sortColumn}
      defaultOrderDirection={header.sort?.dir}
      onOrderChange={onOrderChange}
      pageSize={EvaluationLimits.OverviewPageSize}
      tableLayout="fixed"
      onRowClick={onRowClick}
      onPageChange={page => onPageChange(page + 1)}
      // DataTable's empty-state guard only inspects an array `data`, so a remote table withholds the panel itself.
      detailPanel={totalCount > 0 ? renderPanel : undefined}
    />
  );
};

export default ProjectGroupTable;
